/**
 * @fileoverview World Object V3 schema definitions for artifacts and items.
 *
 * This file contains:
 * - Canonical field definitions for world objects (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All object-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts).
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Object-specific field definitions.
 * Combined with shared fields, these define all attributes of a world object.
 */
const OBJECT_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'appearance',
    label: 'Appearance',
    schemaDescription: "Physical description and visual details",
    initialStateDescription: "What the object looks like at the start of the story.",
    journeyDescription: "What the object looks like at this point in the story.",
  },
  {
    key: 'properties',
    label: 'Properties & Abilities',
    schemaDescription: "Special capabilities or magical properties",
    initialStateDescription: "The object's known powers or special properties.",
    journeyDescription: "The object's powers or properties as currently understood.",
  },
  {
    key: 'origin',
    label: 'Origin & Creation',
    schemaDescription: "How and when it was made",
    initialStateDescription: "What is known about how the object was created.",
    journeyDescription: "What has been revealed about the object's origins.",
  },
  {
    key: 'owner',
    label: 'Current Owner/Location',
    schemaDescription: "Who possesses it or where it is",
    initialStateDescription: "Who has the object or where it is at the start.",
    journeyDescription: "Who currently possesses the object or its current location.",
  },
  {
    key: 'history',
    label: 'History & Provenance',
    schemaDescription: "Past owners and significant events",
    initialStateDescription: "The object's known history before the story begins.",
    journeyDescription: "What has been revealed about the object's past.",
  },
  {
    key: 'value',
    label: 'Value & Rarity',
    schemaDescription: "Worth and scarcity",
    initialStateDescription: "The object's perceived value and rarity.",
    journeyDescription: "The object's current perceived value and rarity.",
  },
  {
    key: 'condition',
    label: 'Condition',
    schemaDescription: "Physical state and any damage",
    initialStateDescription: "The object's physical condition at the start.",
    journeyDescription: "The object's current physical condition.",
  },
  {
    key: 'usage',
    label: 'Usage & Limitations',
    schemaDescription: "How it's used and any restrictions",
    initialStateDescription: "How the object can be used and any known limitations.",
    journeyDescription: "Current understanding of how to use the object and its limits.",
  },
];

/**
 * Complete field definitions for world objects.
 * Combines shared fields with object-specific fields.
 */
export const WORLD_OBJECT_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...OBJECT_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world objects.
 * - artifact: Significant, often unique items with special properties or history
 * - item: General objects of note in the story world, including standalone significant
 *   garments whose meaning is independent of any wearer (e.g., the emperor's cloak)
 * - clothing: An "Outfit" — a character's current outfit / visual look. The internal
 *   enum key remains "clothing" for data compatibility; the user-facing label is "Outfit".
 */
export const WorldObjectElementEnum = z.enum(['artifact', 'item', 'clothing']);
export type WorldObjectElement = z.infer<typeof WorldObjectElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world object attributes schema from the canonical field definitions.
 * All fields are optional for world objects.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_OBJECT_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Object-specific fields
    appearance: z.ZodOptional<z.ZodString>;
    properties: z.ZodOptional<z.ZodString>;
    origin: z.ZodOptional<z.ZodString>;
    owner: z.ZodOptional<z.ZodString>;
    history: z.ZodOptional<z.ZodString>;
    value: z.ZodOptional<z.ZodString>;
    condition: z.ZodOptional<z.ZodString>;
    usage: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world object attributes, used by agents that generate or extract
 * object details. All attributes are optional.
 */
export const WorldObjectAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world object's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldObjectSnapshotV3Schema = WorldObjectAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Object entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldObjectV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world object."),
  name: z.string().describe("The name of the object."),
  type: z.literal('object').describe("Discriminator for world element type."),
  element: WorldObjectElementEnum.describe("The subtype of object (artifact, item, or outfit — internal key 'clothing')."),
  initialDescription: z.string().optional().describe("Brief description of the object before detailed generation."),
  snapshots: z.array(WorldObjectSnapshotV3Schema).describe("Complete timeline of object state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this object."),
  mainImageId: z.string().optional().describe("Reference to the main image for this object."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world object idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldObjectIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the object."),
  element: WorldObjectElementEnum.describe("The proposed subtype (artifact, item, or outfit — internal key 'clothing')."),
  initialDescription: z.string().describe("Brief description of the object concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world objects.
 * Used when only ID and name are needed.
 */
export const WorldObjectRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('object'),
});

/**
 * Basic context schema for world objects.
 * Used for high-level summaries in agent context.
 */
export const WorldObjectBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('object'),
  element: WorldObjectElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world objects.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldObjectContextV3Schema = WorldObjectV3Schema.extend({
  latestSnapshot: WorldObjectSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldObjectAttributes = z.infer<typeof WorldObjectAttributesV3Schema>;
export type WorldObjectSnapshotV3 = z.infer<typeof WorldObjectSnapshotV3Schema>;
export type WorldObjectV3 = z.infer<typeof WorldObjectV3Schema>;
export type WorldObjectIdeaV3 = z.infer<typeof WorldObjectIdeaV3Schema>;
export type WorldObjectRefV3 = z.infer<typeof WorldObjectRefV3Schema>;
export type WorldObjectBasicV3 = z.infer<typeof WorldObjectBasicV3Schema>;
export type WorldObjectContextV3 = z.infer<typeof WorldObjectContextV3Schema>;
